import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CustomerShell } from "@/components/CustomerShell";
import { CareShell } from "@/components/CareShell";
import { useMode } from "@/context/ModeContext";
import { ChevronLeft, Bell, Loader2, ChevronRight, CheckCheck } from "lucide-react";

export default function Notifications() {
    const nav = useNavigate();
    const { mode } = useMode();
    const care = mode === "care";
    const accent = care ? "#F43F5E" : "#009C95";
    const Shell = care ? CareShell : CustomerShell;

    const [list, setList] = useState(null);
    const [open, setOpen] = useState(null);

    const load = () => api.get(`/notifications?mode=${mode}`).then((r) => setList(r.data || [])).catch(() => setList([]));
    useEffect(() => { load(); }, [mode]);

    const markAllRead = async () => { await api.post("/notifications/read").catch(() => {}); load(); };

    const openOne = async (n) => {
        setOpen(n);
        if (!n.read) { await api.post(`/notifications/${n.id}/read`).catch(() => {}); load(); }
    };

    const goRelated = async (n) => {
        if (n.url) { setOpen(null); nav(n.url); return; }
        if (!n.booking_id) return;
        setOpen(null);
        try { await api.get(`/master-bookings/${n.booking_id}`); nav(`/bookings/master/${n.booking_id}`); return; } catch (e) { /* not a master */ }
        try { await api.get(`/bookings/${n.booking_id}`); nav(`/bookings/${n.booking_id}`); return; } catch (e) { /* not a child */ }
        nav("/care/bookings");
    };

    return (
        <Shell noNav>
            <div className="sticky top-0 z-20 flex items-center justify-between px-4 py-4 text-white" style={{ backgroundColor: care ? accent : "#09233B" }}>
                <div className="flex items-center gap-3">
                    <button data-testid="notif-back-btn" onClick={() => nav(-1)} className="rounded-full bg-white/15 p-2 hover:bg-white/25"><ChevronLeft size={20} /></button>
                    <h1 className="font-heading text-lg font-extrabold">Notifications</h1>
                </div>
                <button data-testid="notif-mark-all" onClick={markAllRead} className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold"><CheckCheck size={13} /> Mark all read</button>
            </div>

            <div className="px-4 py-4">
                {list === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin" style={{ color: accent }} /></div>}
                {list && list.length === 0 && (
                    <div className="flex flex-col items-center py-20 text-center">
                        <div className="rounded-full p-5" style={{ backgroundColor: `${accent}1a` }}><Bell size={30} style={{ color: accent }} /></div>
                        <p className="mt-4 font-heading font-bold text-navy">No notifications yet</p>
                        <p className="text-sm text-navy/50">Updates about your bookings and Care appear here.</p>
                    </div>
                )}
                <div className="space-y-2.5">
                    {list?.map((n) => (
                        <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => openOne(n)} className="flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-colors"
                            style={n.read ? { borderColor: "rgba(9,35,59,0.1)", backgroundColor: "#fff" } : { borderColor: `${accent}4d`, backgroundColor: `${accent}0a` }}>
                            <div className="mt-0.5 rounded-xl p-2" style={n.read ? { backgroundColor: "rgba(9,35,59,0.05)", color: "rgba(9,35,59,0.4)" } : { backgroundColor: `${accent}26`, color: accent }}><Bell size={16} /></div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    {!n.read && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />}
                                    <h3 className="font-heading text-sm font-bold text-navy">{n.title}</h3>
                                </div>
                                <p className="mt-0.5 line-clamp-2 text-xs text-navy/60">{n.body}</p>
                                <p className="mt-1 text-[10px] text-navy/35">{new Date(n.created_at).toLocaleString()}</p>
                            </div>
                            <ChevronRight size={16} className="mt-1 shrink-0 text-navy/25" />
                        </button>
                    ))}
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/50" onClick={() => setOpen(null)}>
                    <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()} data-testid="notif-detail">
                        <div className="mb-2 flex items-center gap-2"><div className="rounded-xl p-2" style={{ backgroundColor: `${accent}26`, color: accent }}><Bell size={18} /></div><h3 className="font-heading text-lg font-extrabold text-navy">{open.title}</h3></div>
                        <p className="text-sm text-navy/70">{open.body}</p>
                        <p className="mt-2 text-xs text-navy/40">{new Date(open.created_at).toLocaleString()}</p>
                        {(open.booking_id || open.url) && <button data-testid="notif-view-related" onClick={() => goRelated(open)} className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-full py-3 text-sm font-bold text-white" style={{ backgroundColor: accent }}>View details <ChevronRight size={15} /></button>}
                        <button onClick={() => setOpen(null)} className="mt-2 w-full rounded-full py-2.5 text-sm font-semibold text-navy/55">Close</button>
                    </div>
                </div>
            )}
        </Shell>
    );
}
